import type { Notification } from "../api";
import { useNow } from "../lib/useNow";

/** "12s ago", "5m ago", "3h ago", "2d ago", or a date once it's a week old. */
function agoLabel(ts: number, now: number): string {
  const s = Math.max(0, Math.floor((now - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

/**
 * The notifications inbox, rendered inside the topbar slide-over. Lists the
 * user's notifications newest-first with a severity indicator, title, body, and
 * relative time; each has a dismiss (×), and the whole list a "Mark all read".
 * `reconnect_needed` items deep-link to Settings via {@link onNavigate}.
 */
export function Notifications({
  notifications,
  onDismiss,
  onMarkAllRead,
  onNavigate,
  resolveWorkspace,
}: {
  notifications: Notification[];
  onDismiss: (id: string) => void;
  onMarkAllRead: () => void;
  onNavigate: (n: Notification) => void;
  /** Resolve a notification's `ref.workspaceId` to a brand name to show on the
   * item — so the user knows WHICH workspace it belongs to (notifications span
   * all of them). Returns null to show no chip (e.g. only one workspace). */
  resolveWorkspace?: (workspaceId: string) => string | null;
}) {
  const now = useNow(30_000);

  if (notifications.length === 0) {
    return <p className="notif-empty muted">You're all caught up.</p>;
  }

  const hasUnread = notifications.some((n) => !n.readAt);

  return (
    <div className="notif-list">
      <div className="notif-toolbar">
        <button
          type="button"
          className="link-btn"
          onClick={onMarkAllRead}
          disabled={!hasUnread}
        >
          Mark all read
        </button>
      </div>
      <ul>
        {notifications.map((n) => {
          const actionLabel =
            n.type === "reconnect_needed"
              ? "Reconnect →"
              : n.type === "dispatch_failed"
                ? "View details →"
                : n.type === "credits_low"
                  ? "Top up →"
                  : null;
          const wsId = typeof n.ref?.workspaceId === "string" ? n.ref.workspaceId : null;
          const wsName = wsId && resolveWorkspace ? resolveWorkspace(wsId) : null;
          return (
            <li key={n.id} className={`notif-item ${n.readAt ? "read" : "unread"}`}>
              <span
                className={`notif-dot sev-${n.severity}`}
                aria-hidden="true"
                title={n.severity}
              />
              <div className="notif-main">
                <div className="notif-title">{n.title}</div>
                {n.body && <div className="notif-body small">{n.body}</div>}
                <div className="notif-meta small muted">
                  {wsName && <span className="notif-ws">{wsName}</span>}
                  <span>{agoLabel(n.createdAt, now)}</span>
                  {actionLabel && (
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => onNavigate(n)}
                    >
                      {actionLabel}
                    </button>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="notif-dismiss ghost small"
                aria-label="Dismiss notification"
                onClick={() => onDismiss(n.id)}
              >
                ✕
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
